import {DOCUMENT} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {PlatformService} from './platform.service';
import {environment} from '../../../environments/environment';

interface ScriptOptions {
  src?: string;
  inline?: string;
  async?: boolean;
  attrs?: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class TrackingPixelService {
  private readonly document = inject(DOCUMENT);
  private readonly platformService = inject(PlatformService);
  private initialized = false;

  init(): void {
    if (this.initialized || !this.platformService.isBrowser || !this.document?.head || !environment.enableTrackingPixels) {
      return;
    }

    this.initialized = true;
    this.appendTikTokPixel();
    this.appendGoogleTag();
    this.appendMetaPixel();
  }

  private appendScript(options: ScriptOptions): void {
    const script = this.document.createElement('script');

    if (options.src) {
      script.src = options.src;
      script.async = options.async ?? false;
    }

    if (options.inline) {
      script.textContent = options.inline;
    }

    if (options.attrs) {
      Object.entries(options.attrs).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });
    }

    this.document.head.appendChild(script);
  }

  private appendTikTokPixel(): void {
    this.appendScript({
      inline: `
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;
          var ttq=w[t]=w[t]||[];
          ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
          ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
          for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
          ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
          ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=d.createElement("script");
          n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=d.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
          ttq.load('CV6FNKBC77UC60PKJ3K0');
          ttq.page();
        }(window, document, 'ttq');
      `
    });
  }

  private appendGoogleTag(): void {
    this.appendScript({
      src: 'https://www.googletagmanager.com/gtag/js?id=AW-16886550041',
      async: true
    });

    this.appendScript({
      inline: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-16886550041');
      `
    });
  }

  private appendMetaPixel(): void {
    this.appendScript({
      inline: `
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1208759880669398');
        fbq('track', 'PageView');
      `
    });
  }
}
